import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminEmptyState,
} from "../components/AdminPageShell";
import { useAdminActivityLogs } from "../store/admin";
import {
  formatDate,
  formatTimestamp,
  actorLabel,
  actorTypeLabel,
  actionLabel,
  targetLabel,
  targetTypeLabel,
  actorTypeBadgeClass,
  actionBadgeClass,
  targetTypeBadgeClass,
  metadataLabel,
} from "./components/activityLogs/activityLogUtils";

// ===== ACTIVITY LOG DETAIL PAGE — GET /api/admin/activity-logs/:id =====
// Displays the full detail of a single activity log entry.
const ActivityLogDetail = () => {
  const { logId } = useParams();
  const navigate = useNavigate();

  const {
    activityLogDetail,
    activityLogDetailLoading,
    activityLogDetailError,
    fetchActivityLogById,
  } = useAdminActivityLogs();

  const [isLoaded, setIsLoaded] = useState(false);

  // GET /api/admin/activity-logs/:id
  useEffect(() => {
    if (logId) {
      fetchActivityLogById(logId).finally(() => setIsLoaded(true));
    }
  }, [logId, fetchActivityLogById]);

  const goBack = () => navigate("/admin/activity-logs");

  // Build the detail rows from the fetched log (defensive field resolution).
    const detailRows = activityLogDetail
    ? [
        ["Actor Type", actorTypeLabel(activityLogDetail)],
        ["Actor", actorLabel(activityLogDetail)],
        ["Action", actionLabel(activityLogDetail)],
        ["Target Type", targetTypeLabel(activityLogDetail)],
        ["Target", targetLabel(activityLogDetail)],
        ["Created", activityLogDetail.createdAt || activityLogDetail.created_at || activityLogDetail.timestamp || "--"],
      ]
    : [];

  return (
    <div>
      <AdminNavBar
        title="Activity Logs"
        subTitle="Log Details"
        context="Full audit entry for a single system action."
      />
      <AdminPageBody>
        <div className="mb-3">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Activity Logs
          </button>
        </div>

        {activityLogDetailLoading || (!isLoaded && !activityLogDetailError) ? (
          <AdminLoadingState />
        ) : activityLogDetailError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {activityLogDetailError}
          </div>
        ) : !activityLogDetail ? (
          <AdminEmptyState
            title="Log not found"
            description="The requested activity log could not be found."
          />
        ) : (
          <div className="space-y-6">
            {/* Header with badges */}
            <AdminSectionHeader
              title="Activity Log Detail"
              description={actionLabel(activityLogDetail)}
            />

            {/* Detail table */}
            <AdminTablePanel>
              <table className="w-full border-collapse text-[13px]">
                <tbody>
                  {detailRows.map(([label, value], idx) => (
                    <tr
                      key={label}
                      className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                        idx % 2 === 0 ? "bg-slate-50/40" : ""
                      }`}
                    >
                      <td className="w-48 px-3 py-2.5 font-medium text-slate-600">{label}</td>
                      <td className="px-3 py-2.5 text-slate-900">
                        {label === "Actor Type" ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${actorTypeBadgeClass(
                              value
                            )}`}
                          >
                            {value}
                          </span>
                        ) : label === "Action" ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${actionBadgeClass(
                              value
                            )}`}
                          >
                            {value}
                          </span>
                        ) : label === "Target Type" ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${targetTypeBadgeClass(
                              value
                            )}`}
                          >
                            {value}
                          </span>
                        ) : label === "Created" ? (
                          <div className="flex flex-col">
                            <span>{formatDate(value)}</span>
                            <span className="text-[11px] text-slate-500">
                              {formatTimestamp(value)}
                            </span>
                          </div>
                        ) : (
                          value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTablePanel>

            {/* Details section */}
            {metadataLabel(activityLogDetail) && (
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <h4 className="mb-2 text-sm font-semibold text-slate-700">Metadata</h4>
                <pre className="whitespace-pre-wrap break-all text-[12px] text-slate-700">
                  {metadataLabel(activityLogDetail)}
                </pre>
              </div>
            )}
          </div>
        )}
      </AdminPageBody>
    </div>
  );
};

export default ActivityLogDetail;